import {
  Badge,
  Button,
  Card,
  createTheme,
  Modal,
  NavLink,
  Paper,
  rem,
  Select,
  Table,
  Tabs,
  type MantineThemeOverride,
} from '@mantine/core';
import { generateColors } from '@mantine/colors-generator';
import type { TenantBranding } from '@buildex/module-contract';

/**
 * Тема тенанта из его брендинга. Вся палитра — из одного HEX (§8).
 * Компоненты используют только семантические токены:
 * var(--mantine-color-brand-6), --mantine-radius-default и т.д.
 *
 * Дефолты компонентов задают единый современный вид всей платформы:
 * мягкие карточки с лёгкой тенью, спокойные таблицы, скруглённая навигация.
 * Нейтральные rgba-тени не брендозависимы и не нарушают white-label.
 */
export function buildTenantTheme(b: TenantBranding): MantineThemeOverride {
  return createTheme({
    colors: { brand: generateColors(b.primaryColor) },
    primaryColor: 'brand',
    defaultRadius: b.radius ?? 'md',
    ...(b.fontFamily ? { fontFamily: b.fontFamily } : {}),

    headings: {
      fontWeight: '650',
      sizes: {
        h1: { fontSize: rem(30) },
        h2: { fontSize: rem(24) },
        h3: { fontSize: rem(19) },
      },
    },

    shadows: {
      xs: '0 1px 2px rgba(16, 24, 40, 0.05)',
      sm: '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.05)',
      md: '0 4px 12px rgba(16, 24, 40, 0.08)',
    },

    components: {
      Card: Card.extend({
        defaultProps: { radius: 'lg', padding: 'lg', withBorder: true },
        styles: { root: { boxShadow: 'var(--mantine-shadow-xs)' } },
      }),
      Paper: Paper.extend({
        defaultProps: { radius: 'lg' },
      }),
      Button: Button.extend({
        defaultProps: { radius: 'md' },
        styles: { root: { fontWeight: 600 } },
      }),
      Badge: Badge.extend({
        defaultProps: { radius: 'sm', variant: 'light' },
        styles: { root: { textTransform: 'none', fontWeight: 600 } },
      }),
      Table: Table.extend({
        defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'md' },
      }),
      NavLink: NavLink.extend({
        defaultProps: { variant: 'light' },
        styles: {
          root: { borderRadius: 'var(--mantine-radius-md)', marginBottom: rem(2) },
          label: { fontWeight: 500 },
        },
      }),
      Select: Select.extend({
        defaultProps: { checkIconPosition: 'right' },
      }),
      Tabs: Tabs.extend({
        styles: { tab: { fontWeight: 500 } },
      }),
      Modal: Modal.extend({
        defaultProps: { radius: 'lg', centered: true },
      }),
    },
  });
}
